import React, { useState, useEffect } from 'react';
import { PurchaseOrder, ReceivePORequest, ReceivePOItem, receivePurchaseOrder } from '../../services/purchaseOrderService';

interface ReceivePOModalProps {
  po: PurchaseOrder;
  onClose: () => void;
  onSuccess: () => void;
}

const ReceivePOModal: React.FC<ReceivePOModalProps> = ({ po, onClose, onSuccess }) => {
  const [receivingItems, setReceivingItems] = useState<ReceivePOItem[]>([]);
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Initialize dengan items yang masih bisa diterima
    const initialItems = po.items
      .filter(item => item.qty_received < item.qty_ordered)
      .map(item => ({
        item_id: item.item_id,
        qty_received: item.qty_ordered - item.qty_received, // Default receive semua sisa
      }));
    setReceivingItems(initialItems);
  }, [po]);

  const handleQtyChange = (itemId: number, qty: number) => {
    setReceivingItems(prev =>
      prev.map(item =>
        item.item_id === itemId ? { ...item, qty_received: Math.max(0, qty) } : item
      )
    );
  };

  const handleSubmit = async () => {
    try {
      setError(null);
      setLoading(true);

      // Filter hanya items dengan qty > 0
      const itemsToReceive = receivingItems.filter(item => item.qty_received > 0);

      if (itemsToReceive.length === 0) {
        setError('Tidak ada item yang akan diterima');
        setLoading(false);
        return;
      }

      // Validasi qty tidak melebihi yang bisa diterima
      for (const receivingItem of itemsToReceive) {
        const poItem = po.items.find(i => i.item_id === receivingItem.item_id);
        if (poItem) {
          const maxCanReceive = poItem.qty_ordered - poItem.qty_received;
          if (receivingItem.qty_received > maxCanReceive) {
            setError(
              `Qty untuk ${poItem.item_name} melebihi sisa yang bisa diterima (max: ${maxCanReceive})`
            );
            setLoading(false);
            return;
          }
        }
      }

      const receiveData: ReceivePORequest = {
        items: itemsToReceive,
        notes: notes || undefined,
      };

      await receivePurchaseOrder(po.id, receiveData);
      onSuccess();
      onClose();
    } catch (err: any) {
      setError(err.response?.data?.detail || err.message || 'Failed to receive items');
    } finally {
      setLoading(false);
    }
  };

  const getPendingQty = (itemId: number): number => {
    const item = po.items.find(i => i.item_id === itemId);
    return item ? item.qty_ordered - item.qty_received : 0;
  };

  const getTotalReceiving = (): number => {
    return receivingItems.reduce((sum, item) => sum + item.qty_received, 0);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl mx-4 max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="p-6 border-b">
          <h2 className="text-2xl font-bold text-gray-800">Receive Items - PO {po.po_number}</h2>
          <p className="text-sm text-gray-600 mt-1">
            Status: <span className="font-semibold">{po.status}</span>
          </p>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto flex-1">
          {error && (
            <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
              {error}
            </div>
          )}

          {/* Items List */}
          <div className="mb-6">
            <h3 className="text-lg font-semibold mb-3">Items to Receive</h3>
            <div className="space-y-3">
              {po.items
                .filter(item => item.qty_received < item.qty_ordered)
                .map(item => {
                  const receivingItem = receivingItems.find(ri => ri.item_id === item.item_id);
                  const pendingQty = getPendingQty(item.item_id);

                  return (
                    <div key={item.id} className="border rounded-lg p-4 bg-gray-50">
                      <div className="flex justify-between items-start mb-3">
                        <div className="flex-1">
                          <h4 className="font-semibold text-gray-800">{item.item_name}</h4>
                          <p className="text-sm text-gray-600">SKU: {item.item_sku}</p>
                        </div>
                        <div className="text-right text-sm">
                          <div className="text-gray-600">
                            Ordered: <span className="font-semibold">{item.qty_ordered}</span>
                          </div>
                          <div className="text-gray-600">
                            Already Received: <span className="font-semibold">{item.qty_received}</span>
                          </div>
                          <div className="text-blue-600 font-semibold">
                            Pending: {pendingQty}
                          </div>
                        </div>
                      </div>

                      <div className="flex items-center gap-4">
                        <label className="text-sm font-medium text-gray-700 w-32">
                          Qty to Receive:
                        </label>
                        <input
                          type="number"
                          min="0"
                          max={pendingQty}
                          value={receivingItem?.qty_received || 0}
                          onChange={(e) =>
                            handleQtyChange(item.item_id, parseInt(e.target.value) || 0)
                          }
                          className="w-32 px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500"
                        />
                        <span className="text-sm text-gray-600">of {pendingQty} pending</span>
                      </div>
                    </div>
                  );
                })}
            </div>
          </div>

          {/* Notes */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Notes (Optional)
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              className="w-full px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500"
              placeholder="Add notes about this receipt..."
            />
          </div>

          {/* Summary */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex justify-between items-center">
              <span className="font-semibold text-gray-700">Total Items Receiving:</span>
              <span className="text-2xl font-bold text-blue-600">{getTotalReceiving()}</span>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="p-6 border-t bg-gray-50 flex justify-end gap-3">
          <button
            onClick={onClose}
            disabled={loading}
            className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-100 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={loading || getTotalReceiving() === 0}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? 'Processing...' : 'Receive Items'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ReceivePOModal;
